import { Box, Button, Chip, Dialog, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import hasPermissions from "../../utils/hasPermissions";
import { PERMISSIONS } from "../../utils/constants";
import EditContentTabs from "../../components/editContentTabs";
import { getContent, setContent } from "../../api/content";
import { useSelector } from "react-redux";
import { approveCompetition, getCompetitions, putCompetition, unapproveCompetition } from "../../api/competitions";
import ManageFieldsDialog from "../../components/customApiBlock/manageFieldsDialog";
import { fetchAllSports } from "../../api/sports";

const createColumns = (handleOpenManageContentDialog, handleOpenEditDialog, toggleApprove) => {
  return [
    {
      field: "id",
      headerName: "Id",
      flex: 1,
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
    },
    {
      field: "urlSegment",
      headerName: "Url segment",
      flex: 1,
    },
    {
      field: "visible",
      headerName: "Visible",
      flex: 1,
      sortable: false,
      renderCell: (row) => row.row.visible
        ? <Chip color="success" variant="filled" size="medium" label={'Visible'} />
        : <Chip color="error" variant="filled" size="medium" label={'Not visible'} />
    },
    {
      field: "approved",
      headerName: "Approved",
      flex: 1,
      sortable: false,
      renderCell: (row) =>
        <Button
          variant="contained"
          color={row.row.approved ? 'error' : 'success'}
          disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_COMPETITIONS)}
          onClick={() => toggleApprove(row.row)}
        >
          {row.row.approved ? 'Unapprove' : 'Approve'}
        </Button>
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1.5,
      sortable: false,
      renderCell: ({ row }) => (
        <Box
          sx={{
            display: "flex",
            gap: "10px",
            padding: '5px',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_COMPETITIONS)}
            onClick={() => handleOpenManageContentDialog(row)}
          >
            Content
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!hasPermissions(PERMISSIONS.ALLOW_WRITE_COMPETITIONS)}
            onClick={() => handleOpenEditDialog(row)}
          >
            Edit
          </Button>
        </Box>
      ),
    },
  ];
};





export default function Competitions() {
  const projectId = useSelector((state) => state.project.currentProjectId);
  const source = useSelector((state) => state.source.currentSource);
  const [competitions, setCompetitions] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [manageContentDialogOpen, setManageContentDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [currentCompetition, setCurrentCompetition] = useState(null);
  const [pageSize, setPageSize] = useState(25);
  const [sports, setSports] = useState(null);
  const [filters, setFilters] = useState({
    urlSegment: "",
    name: "",
    sportId: "",
  });
  const [tempFilters, setTempFilters] = useState({
    urlSegment: "",
    name: "",
    sportId: "",
  });

    const closeManageContentDialog = () => {
      setManageContentDialogOpen(false);
      setCurrentCompetition(null);
    };
  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setCurrentCompetition(null);
  };

  const createParams = useCallback(() => {
    const filterParams = [];
    Object.keys(filters).forEach((key) => {
      if (!filters[key]) return;
      filterParams.push({ key, value: filters[key] });
    });
    const pageParams = [
      { key: "take", value: pageSize },
      { key: "skip", value: page * pageSize },
    ];
    const sourceParams = [{ key: "source", value: source }];
    return [...filterParams, ...pageParams, ...sourceParams];
  }, [filters, page, pageSize, source]);

  const handleFiltersChange = useCallback(
    (event) => {
      const { name, value } = event.target;
      setTempFilters({ ...tempFilters, [name]: value });
    },
    [tempFilters]
  );

  const handleSearchClick = useCallback(() => {
    setPage(0);
    setFilters(tempFilters);
  }, [tempFilters]);

  const getCompetitionList = useCallback(async () => {
    if (!source) {
      return;
    }
    const queryParams = createParams();
    const res = await getCompetitions(queryParams);
    setCompetitions(res.data);
    setTotal(res.total);
  }, [createParams, source]);

  const handleResetFilters = useCallback(async () => {
    setFilters({
      urlSegment: "",
      name: "",
    });
    setTempFilters({
      urlSegment: "",
      name: "",
    });
    setPage(0);
  }, []);

    const handleOpenManageContentDialog = useCallback((item) => {
      setCurrentCompetition(item);
      setManageContentDialogOpen(true);
    }, []);

  const handleOpenEditDialog = useCallback((item) => {
    setCurrentCompetition(item);
    setEditDialogOpen(true);
  }, []);

  const toggleApprove = useCallback(async (item) => {
    if (!item.approved) {
      await approveCompetition(item.id);
    } else {
      await unapproveCompetition(item.id);
    }
    getCompetitionList();
  }, [getCompetitionList]);

  const editFields = useMemo(
    () => [
      { name: "customName", label: "Custom name", type: "text", required: false },
    ],
    []
  );

  const handleEditConfirm = useCallback(
    async (payload) => {
      if (!currentCompetition?.id) {
        return;
      }
      await putCompetition(currentCompetition.id, {
        customName: payload.customName ?? "",
      });
      await getCompetitionList();
      setEditDialogOpen(false);
      setCurrentCompetition(null);
    },
    [currentCompetition, getCompetitionList]
  );

  const columns = useMemo(() => {
    return createColumns(handleOpenManageContentDialog, handleOpenEditDialog, toggleApprove);
  }, [handleOpenManageContentDialog, handleOpenEditDialog, toggleApprove]);

  const handlePageSizeChange = useCallback((newPageSize) => {
    setPage(0);
    setPageSize(newPageSize);
  }, []);

  const contentKeys = [
    { title: "Teams", contentPostfix: "Competitors" },
    { title: "Players", contentPostfix: "Players" },
  ];

  const handleGetCatalogContent = useCallback(
    async (contentKey) => {
      const competition = currentCompetition || {};
      const data = await getContent(projectId, 'Competition', `${competition.id}-${contentKey}`);
      return data;
    },
    [projectId, currentCompetition]
  );

  const handleSetCatalogContent = useCallback(
    async (contentKey, content) => {
      const competition = currentCompetition || {};
      const data = await setContent(projectId, 'Competition', `${competition.id}-${contentKey}`, content);
      return data;
    },
    [projectId, currentCompetition]
  );

  const fetchSports = useCallback(async () => {
    const list = await fetchAllSports();
    setSports(list);
  }, []);

  useEffect(() => {
    fetchSports();
  }, [fetchSports]);

  useEffect(() => {
    getCompetitionList();
  }, [getCompetitionList])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px' }}>
      <h1>Competitions</h1>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          height: "100px",
        }}
      >
        filter by
        <TextField
          name="name"
          variant="outlined"
          size="small"
          label="Name"
          value={tempFilters.name}
          onChange={(event) => handleFiltersChange(event)}
        />
        <TextField
          name="urlSegment"
          variant="outlined"
          size="small"
          label="Url Segment"
          value={tempFilters.urlSegment}
          onChange={(event) => handleFiltersChange(event)}
        />
        {sports && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="competitions-sport-filter-label">Sport</InputLabel>
            <Select
              labelId="competitions-sport-filter-label"
              label="Sport"
              name="sportId"
              value={tempFilters.sportId}
              onChange={(event) => handleFiltersChange(event)}
            >
              <MenuItem value="">
                <em>All sports</em>
              </MenuItem>
              {sports.map((sport) => (
                <MenuItem key={sport.id} value={sport.id}>
                  {sport.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button variant="contained" onClick={() => handleSearchClick()}>
          Search
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => handleResetFilters()}
        >
          Reset
        </Button>
      </div>
      {competitions && <Box sx={{ height: "700px", width: "100%" }}>
        <DataGrid
          getRowHeight={() => 'auto'}
          rows={competitions}
          columns={columns}
          rowCount={total}
          pageSize={pageSize}
          page={page}
          paginationMode="server"
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[25, 50, 100]}
          disableSelectionOnClick
          disableColumnFilter
          experimentalFeatures={{ newEditingApi: true }}
        />
      </Box>}
      {currentCompetition && manageContentDialogOpen &&
        <Dialog fullScreen open={manageContentDialogOpen} onClose={closeManageContentDialog}>
          <Box>
            <EditContentTabs
              item={{ contentKey: '' }}
              keys={contentKeys}
              closeDialog={closeManageContentDialog}
              getContent={handleGetCatalogContent}
              setContent={handleSetCatalogContent}
            />
          </Box>
        </Dialog>}
      {editDialogOpen && currentCompetition && (
        <ManageFieldsDialog
          open={editDialogOpen}
          onClose={closeEditDialog}
          onConfirm={handleEditConfirm}
          data={{ ...currentCompetition, customName: currentCompetition.customName ?? "" }}
          fields={editFields}
          title={currentCompetition.name || "Edit competition"}
        />
      )}
    </div>
  );
}
